import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Dropdown, Button, Tooltip, Icon, Input } from '@sm/ide-components';
import { useChatStore } from '@/store';
import { StopIcon, SendIcon } from '@/assets/SVGIcon';
import { EAnalyticsMode, ERole } from '@/types';
import { t } from '@/i18n';
import { common, getWebFetchMode, setWebFetchEnabled } from '@/common';
import { getMacawRoot } from '@/utils/macawRoot';
import { isMindSource } from '@/utils/mindHost';
import MindMentionEditor, { type MindMentionEditorHandle } from './MindMentionEditor';
import './index.less';

interface ChatInputProps {
  onSend: (text: string) => void;
  onTerminate?: () => void;
  disabled?: boolean;
  terminateLoading?: boolean;
  showTerminateButton?: boolean;
  /** init.source。不传时读 common.source */
  hostSource?: string;
}

const { TextArea } = Input;
// 输入token限制
const MAX_TOKEN = 4096;
const MIN_LINES = 3;
const MAX_LINES = 10;

// font-size: 14px，line-height: 1.6，每行约 22.4px，稍微放大一点避免截断
const LINE_HEIGHT = 24;

function WebSearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <circle cx="8" cy="8" r="6.15" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <ellipse cx="8" cy="8" rx="2.65" ry="6.15" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <path
        d="M2.15 8h11.7M3.2 5.15h9.6M3.2 10.85h9.6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
    </svg>
  );
}

const ANALYTICS_MODE_KEYS: { key: EAnalyticsMode; labelKey: string }[] = [
  { key: EAnalyticsMode.FAST, labelKey: 'chatInput.modeQuick' },
  { key: EAnalyticsMode.SEGMENT, labelKey: 'chatInput.modeSegment' },
];
/**
 * 估算文本行数：按 \n 分段
 */
const estimateLines = (text: string): number => {
  if (!text) return MIN_LINES;
  return text.split('\n').length ?? 3;
};

const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  onTerminate,
  disabled,
  terminateLoading,
  showTerminateButton,
  hostSource,
}) => {
  const [value, setValue] = useState('');
  const [webSearch, setWebSearch] = useState(() => getWebFetchMode() === 'auto');
  const [textareaHeight, setTextareaHeight] = useState<number | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mentionRef = useRef<MindMentionEditorHandle>(null);
  const valueRef = useRef('');
  const mentionEnabled = isMindSource(hostSource !== undefined ? hostSource : common.source);

  useEffect(() => {
    if (!mentionEnabled) return;
    setWebFetchEnabled(webSearch);
  }, [mentionEnabled, webSearch]);

  const {
    currentRole,
    roles,
    setCurrentRole,
    setAnalyticsMode,
    analyticsMode,
  } = useChatStore();

  const otherHeight = currentRole.id === ERole.BUSINESS_ANALYSIS ? 44 : 74;
  const adjustHeight = useCallback((_el: HTMLTextAreaElement, text: string) => {
    const lines = estimateLines(text);
    const clampedLines = Math.min(Math.max(lines, MIN_LINES), MAX_LINES);
    const newHeight = clampedLines * LINE_HEIGHT;
    setTextareaHeight(newHeight);
  }, []);

  const applyValue = useCallback((text: string) => {
    valueRef.current = text;
    setValue(text);
    if (textareaRef.current) {
      adjustHeight(textareaRef.current, text);
    } else {
      const lines = estimateLines(text);
      const clampedLines = Math.min(Math.max(lines, MIN_LINES), MAX_LINES);
      setTextareaHeight(clampedLines * LINE_HEIGHT);
    }
  }, [adjustHeight]);

  const handleSend = useCallback((text?: string) => {
    const trimmed = (text ?? value).trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    if (mentionRef.current) {
      mentionRef.current.clear();
      valueRef.current = '';
      setValue('');
      return;
    }
    applyValue('');
    setTextareaHeight(null);
  }, [value, disabled, onSend, applyValue]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    valueRef.current = text;
    setValue(text);
    adjustHeight(e.target, text);
  };

  const roleMenuItems = roles.map((role) => ({
    key: role.id,
    label: role.label,
    onClick: () => setCurrentRole(role),
    disabled: Boolean(role.isDisabled),
  }));

  const canSend = value.trim().length > 0 && !disabled;
  const isAnalyst = currentRole.id === ERole.ANALYST;

  const modeMenuItems = ANALYTICS_MODE_KEYS.map(({ key, labelKey }) => ({
    key,
    label: t(labelKey),
    onClick: () => setAnalyticsMode(key),
  }));

  const currentModeLabel =
    t(ANALYTICS_MODE_KEYS.find((m) => m.key === analyticsMode)?.labelKey ?? 'chatInput.modeQuick');
  const isProcessing = Boolean(showTerminateButton);

  return (
    <div className="chat-input-wrap">
      <div
        className="chat-input-box"
        style={textareaHeight !== null ? { height: textareaHeight + otherHeight } : undefined}
      >
        {isAnalyst && (
          <div className="chat-input-mode-bar">
            <Dropdown
              menu={{ items: modeMenuItems, selectedKeys: [analyticsMode] }}
              trigger={['click']}
              placement="topLeft"
              overlayClassName="chat-input-dropdown"
              getPopupContainer={getMacawRoot}
              disabled={disabled}
            >
              <div
                className="chat-input-mode-selector"
                style={disabled ? { opacity: 0.45, cursor: 'not-allowed', pointerEvents: 'none' } : undefined}
              >
                <span className="chat-input-mode-selector__label">{currentModeLabel}</span>
                <Icon name="icon-ic_arrow_down" size={12} />
              </div>
            </Dropdown>
          </div>
        )}
        {mentionEnabled ? (
          <MindMentionEditor
            ref={mentionRef}
            disabled={disabled}
            maxLength={MAX_TOKEN}
            placeholder={t('chatInput.placeholderMind')}
            onChange={applyValue}
            onSubmit={handleSend}
            onHeight={setTextareaHeight}
          />
        ) : (
          <TextArea
            ref={textareaRef}
            className="chat-input-textarea"
            value={value}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            placeholder={t('chatInput.placeholder')}
            rows={MIN_LINES}
            autoSize={false}
            disabled={disabled}
            maxLength={MAX_TOKEN}
            autoFocus={true}
            style={{
              resize: 'none',
              overflowY: 'auto',
              ...(textareaHeight !== null && { height: textareaHeight }),
            }}
          />
        )}

        <div className="chat-input-entry">
          <div className="chat-input-entry-leading">
            <div className="chat-input-entry-role">
              <Dropdown
                menu={{ items: roleMenuItems, selectedKeys: [currentRole.id] }}
                trigger={['click']}
                placement="topLeft"
                overlayClassName="chat-input-dropdown"
                getPopupContainer={getMacawRoot}
                disabled={disabled}
              >
                <div
                  className="chat-input-entry-role__trigger"
                  style={disabled ? { opacity: 0.45, cursor: 'not-allowed', pointerEvents: 'none' } : undefined}
                >
                  <span className="chat-input-entry-role__name" onClick={(e) => e.preventDefault()}>
                    {currentRole.label}
                  </span>
                  <Icon name="icon-ic_arrow_down" size={12} />
                </div>
              </Dropdown>
            </div>
            {mentionEnabled && (
              <button
                type="button"
                className={`chat-input-web-search${webSearch ? ' is-active' : ''}`}
                aria-pressed={webSearch}
                disabled={disabled}
                onClick={() => setWebSearch((on) => !on)}
              >
                <WebSearchIcon />
                <span>{t('chatInput.webSearch')}</span>
              </button>
            )}
          </div>
          <div className="chat-input-entry-actions">
            {isProcessing ? (
              <Tooltip title={t('chatInput.stop')}>
                <Button
                  size="small"
                  icon={<StopIcon />}
                  className="chat-input-text-send"
                  loading={terminateLoading}
                  disabled={terminateLoading}
                  onClick={onTerminate}
                />
              </Tooltip>
            ) : (
              <Button
                size="small"
                icon={<SendIcon />}
                className="chat-input-text-send"
                onClick={() => handleSend()}
                disabled={!canSend}
              />
            )}
          </div>
        </div>
      </div>

      <p className="chat-input-disclaimer">{t('chatInput.disclaimer')}</p>
    </div>
  );
};

export default ChatInput;
